<template>
  <div class="container" v-if="!store.loggedIn">
    <div class="registrationContainer cardBackground">
      <h1>Register</h1>

      <!-- Page 1: Personal Information -->
      <div v-if="currentPage === 1" class="registration-page">
        <div class="input-container">
          <input class="registrationInput" type="text" v-model="firstName" @blur="validateField('firstName')" :class="{ 'error': errors.firstName }" placeholder="First Name" style="border-color: #45403C;">
          <div v-if="errors.firstName" class="error-message">{{ errors.firstName }}</div>
        </div>

        <div class="input-container">
          <input class="registrationInput" type="text" v-model="lastName" @blur="validateField('lastName')" :class="{ 'error': errors.lastName }" placeholder="Last Name" style="border-color: #45403C;">
          <div v-if="errors.lastName" class="error-message">{{ errors.lastName }}</div>
        </div>

        <div class="input-container">
          <input class="registrationInput" type="tel" v-model="phoneNumber" @blur="validateField('phoneNumber')" :class="{ 'error': errors.phoneNumber }" placeholder="Phone Number" style="border-color: #45403C;">
          <div v-if="errors.phoneNumber" class="error-message">{{ errors.phoneNumber }}</div>
        </div>

        <div class="input-container">
          <input class="registrationInput" type="text" v-model="address" @blur="validateField('address')" :class="{ 'error': errors.address }" placeholder="Address" style="border-color: #45403C;">
          <div v-if="errors.address" class="error-message">{{ errors.address }}</div>
        </div>

        <div class="input-container">
          <input class="registrationInput" type="text" v-model="address2" placeholder="Address 2 (Optional)" style="border-color: #45403C;">
        </div>

        <div class="input-container">
          <input class="registrationInput" type="text" v-model="city" @blur="validateField('city')" :class="{ 'error': errors.city }" placeholder="City" style="border-color: #45403C;">
          <div v-if="errors.city" class="error-message">{{ errors.city }}</div>
        </div>

        <div class="input-container">
          <CustomSelect
            :options="stateOptions"
            v-model="selectedState"
            placeholder="State"
            :searchable="true"
            @change="validateField('selectedState')"
            :match-input-style="true"
            :custom-style="{ borderColor: errors.selectedState ? '#dc3545' : 'var(--color-border-input)' }"
          />
          <div v-if="errors.selectedState" class="error-message">{{ errors.selectedState }}</div>
        </div>

        <div class="input-container">
          <input class="registrationInput" type="text" v-model="zipCode" @blur="validateField('zipCode')" :class="{ 'error': errors.zipCode }" placeholder="ZIP Code" style="border-color: #45403C;">
          <div v-if="errors.zipCode" class="error-message">{{ errors.zipCode }}</div>
        </div>

        <div class="button-container">
          <button class="back-button" @click="goBack">Back</button>
          <button class="specialButton" @click="nextPage" style="color: var(--text-primary); background-color: var(--color-secondary-button)">Next</button>
        </div>
      </div>

      <!-- Page 2: Account Information -->
      <div v-if="currentPage === 2" class="registration-page">
        <div class="input-container">
          <input class="registrationInput" type="email" v-model="email" @blur="validateField('email')" :class="{ 'error': errors.email }" placeholder="Email" style="border-color: #45403C;">
          <div v-if="errors.email" class="error-message">{{ errors.email }}</div>
        </div>

        <div class="input-container">
          <input class="registrationInput" type="text" v-model="username" @blur="validateField('username')" :class="{ 'error': errors.username }" placeholder="Username" style="border-color: #45403C;">
          <div v-if="errors.username" class="error-message">{{ errors.username }}</div>
        </div>

        <div class="input-container">
          <input class="registrationInput" :type="viewPassword ? 'text' : 'password'" v-model="password" @blur="validateField('password')" :class="{ 'error': errors.password }" placeholder="Password" style="border-color: #45403C;">
          <svg @click="togglePasswordVisibility" class="viewPasswordIcon" :class="viewPassword ? 'colorSwap' : ''"><use href="/static/icons/Eye.svg#Eye"/></svg>
          <div v-if="errors.password" class="error-message">{{ errors.password }}</div>
        </div>

        <div class="input-container">
          <input class="registrationInput" :type="viewConfirmPassword ? 'text' : 'password'" v-model="confirmPassword" @blur="validateField('confirmPassword')" :class="{ 'error': errors.confirmPassword }" placeholder="Confirm Password" style="border-color: #45403C;">
          <svg @click="toggleConfirmPasswordVisibility" class="viewPasswordIcon" :class="viewConfirmPassword ? 'colorSwap' : ''"><use href="/static/icons/Eye.svg#Eye"/></svg>
          <div v-if="errors.confirmPassword" class="error-message">{{ errors.confirmPassword }}</div>
        </div>

        <div class="button-container">
          <button class="specialButton secondary" @click="previousPage" style="color: var(--text-primary); background-color: var(--color-border-input); margin-right: 10px;">Back</button>
          <button class="specialButton" @click="register" style="color: var(--text-primary); background-color: var(--color-secondary-button)">Register</button>
        </div>
      </div>
    </div>
    <div></div>
    <MessageModal :message="appMessage.text" :type="appMessage.type" ref="messageModal" />
    <LoadingPage :is-visible="loading"/>
  </div>
</template>

<script>
import MessageModal from "@/components/MessageModal.vue";
import LoadingPage from "@/components/LoadingPage.vue";
import CustomSelect from "@/components/CustomSelect.vue";
import CommonAPIService from "@/servicehandlers/CommonHandler";
import {useLoginStore} from "@/stores/loginStore";

const commonService = new CommonAPIService()

export default {
  name: 'UserRegistrationPage',
  components: {LoadingPage, MessageModal, CustomSelect},
  data(){
    return {
      currentPage: 1,
      appMessage: {
        text: '',
        type: 'error',
      },
      loading: false,
      store: useLoginStore(),
      
      // Page 1 fields
      firstName: '',
      lastName: '',
      phoneNumber: '',
      address: '',
      address2: '',
      city: '',
      selectedState: null,
      zipCode: '',
      
      // Page 2 fields
      email: '',
      username: '',
      password: '',
      confirmPassword: '',
      
      // UI state
      viewPassword: false,
      viewConfirmPassword: false,
      
      // Validation
      errors: {},
      
      // State options
      stateOptions: [
        { id: 'AL', name: 'AL' },
        { id: 'AK', name: 'AK' },
        { id: 'AZ', name: 'AZ' },
        { id: 'AR', name: 'AR' },
        { id: 'CA', name: 'CA' },
        { id: 'CO', name: 'CO' },
        { id: 'CT', name: 'CT' },
        { id: 'DE', name: 'DE' },
        { id: 'FL', name: 'FL' },
        { id: 'GA', name: 'GA' },
        { id: 'HI', name: 'HI' },
        { id: 'ID', name: 'ID' },
        { id: 'IL', name: 'IL' },
        { id: 'IN', name: 'IN' },
        { id: 'IA', name: 'IA' },
        { id: 'KS', name: 'KS' },
        { id: 'KY', name: 'KY' },
        { id: 'LA', name: 'LA' },
        { id: 'ME', name: 'ME' },
        { id: 'MD', name: 'MD' },
        { id: 'MA', name: 'MA' },
        { id: 'MI', name: 'MI' },
        { id: 'MN', name: 'MN' },
        { id: 'MS', name: 'MS' },
        { id: 'MO', name: 'MO' },
        { id: 'MT', name: 'MT' },
        { id: 'NE', name: 'NE' },
        { id: 'NV', name: 'NV' },
        { id: 'NH', name: 'NH' },
        { id: 'NJ', name: 'NJ' },
        { id: 'NM', name: 'NM' },
        { id: 'NY', name: 'NY' },
        { id: 'NC', name: 'NC' },
        { id: 'ND', name: 'ND' },
        { id: 'OH', name: 'OH' },
        { id: 'OK', name: 'OK' },
        { id: 'OR', name: 'OR' },
        { id: 'PA', name: 'PA' },
        { id: 'RI', name: 'RI' },
        { id: 'SC', name: 'SC' },
        { id: 'SD', name: 'SD' },
        { id: 'TN', name: 'TN' },
        { id: 'TX', name: 'TX' },
        { id: 'UT', name: 'UT' },
        { id: 'VT', name: 'VT' },
        { id: 'VA', name: 'VA' },
        { id: 'WA', name: 'WA' },
        { id: 'WV', name: 'WV' },
        { id: 'WI', name: 'WI' },
        { id: 'WY', name: 'WY' },
      ]
    }
  },
  async mounted(){
    //console.clear()
    if (this.store.loggedIn){
      this.goToHome()
    }
  },
  methods: {
    validateField(field) {
      this.errors = { ...this.errors };
      
      switch(field) {
        case 'firstName':
          if (!this.firstName.trim()) {
            this.errors.firstName = 'First name is required';
          } else {
            delete this.errors.firstName;
          }
          break;
          
        case 'lastName':
          if (!this.lastName.trim()) {
            this.errors.lastName = 'Last name is required';
          } else {
            delete this.errors.lastName;
          }
          break;
          
        case 'phoneNumber':
          if (!this.phoneNumber.trim()) {
            this.errors.phoneNumber = 'Phone number is required';
          } else {
            delete this.errors.phoneNumber;
          }
          break;
          
        case 'address':
          if (!this.address.trim()) {
            this.errors.address = 'Address is required';
          } else {
            delete this.errors.address;
          }
          break;
          
        case 'city':
          if (!this.city.trim()) {
            this.errors.city = 'City is required';
          } else {
            delete this.errors.city;
          }
          break;
          
        case 'selectedState':
          if (!this.selectedState) {
            this.errors.selectedState = 'State is required';
          } else {
            delete this.errors.selectedState;
          }
          break;
          
        case 'zipCode':
          if (!this.zipCode.trim()) {
            this.errors.zipCode = 'ZIP code is required';
          } else {
            delete this.errors.zipCode;
          }
          break;
          
        case 'email':
          if (!this.email.trim()) {
            this.errors.email = 'Email is required';
          } else if (!/\S+@\S+\.\S+/.test(this.email)) {
            this.errors.email = 'Email format is invalid';
          } else {
            delete this.errors.email;
          }
          break;
          
        case 'username':
          if (!this.username.trim()) {
            this.errors.username = 'Username is required';
          } else {
            delete this.errors.username;
          }
          break;
          
        case 'password':
          if (!this.password.trim()) {
            this.errors.password = 'Password is required';
          } else if (this.password.length < 6) {
            this.errors.password = 'Password must be at least 6 characters';
          } else {
            delete this.errors.password;
          }
          if (this.confirmPassword && this.password !== this.confirmPassword) {
            this.errors.confirmPassword = 'Passwords do not match';
          } else if (this.confirmPassword && this.password === this.confirmPassword) {
            delete this.errors.confirmPassword;
          }
          break;
          
        case 'confirmPassword':
          if (!this.confirmPassword.trim()) {
            this.errors.confirmPassword = 'Password confirmation is required';
          } else if (this.password !== this.confirmPassword) {
            this.errors.confirmPassword = 'Passwords do not match';
          } else {
            delete this.errors.confirmPassword;
          }
          break;
      }
    },
    
    validatePage1() {
      const page1Fields = ['firstName', 'lastName', 'phoneNumber', 'address', 'city', 'selectedState', 'zipCode'];
      page1Fields.forEach(field => this.validateField(field));
      
      return page1Fields.every(field => !this.errors[field]);
    },
    
    validatePage2() {
      const page2Fields = ['email', 'username', 'password', 'confirmPassword'];
      page2Fields.forEach(field => this.validateField(field));
      
      return page2Fields.every(field => !this.errors[field]);
    },
    
    nextPage() {
      if (this.validatePage1()) {
        this.currentPage = 2;
      } else {
        this.appMessage.text = 'Please fill in all required fields';
        this.appMessage.type = 'error';
        this.$refs.messageModal.open();
      }
    },
    
    previousPage() {
      this.currentPage = 1;
    },
    
    togglePasswordVisibility() {
      this.viewPassword = !this.viewPassword;
    },
    
    toggleConfirmPasswordVisibility() {
      this.viewConfirmPassword = !this.viewConfirmPassword;
    },
    
    register() {
      if (this.validatePage2()) {
        this.loading = true;
        
        const registrationData = {
          firstName: this.firstName,
          lastName: this.lastName,
          phoneNumber: this.phoneNumber,
          address: this.address,
          address2: this.address2,
          city: this.city,
          state: this.selectedState,
          zipCode: this.zipCode,
          email: this.email,
          username: this.username,
          password: this.password
        };
        
        return commonService
          .registerBasic(registrationData, this.$router, this.store, [])
          .then((response) => {
            if (response && response.token) {
              this.goToHome();
            } else {
              this.appMessage.text = response.msg || 'Registration failed. Please try again.';
              this.appMessage.type = 'error';
              this.$refs.messageModal.open();
            }
            this.loading = false;
          })
          .catch(e => {
            this.appMessage.text = 'Registration error: ' + (e.message || e);
            this.appMessage.type = 'error';
            this.$refs.messageModal.open();
            this.loading = false;
          });
        
      } else {
        this.appMessage.text = 'Please fill in all required fields correctly';
        this.appMessage.type = 'error';
        this.$refs.messageModal.open();
      }
    },
    
    showError(e){
      this.appMessage.text = 'Error: ' + e
      this.appMessage.type = 'error'
      this.$refs.messageModal.open()
    },
    
    async goToHome(){
      this.$router.push({
        name: 'dashboard'
      })
    },
    
    goBack(){
      this.$router.go(-1)
    },
  },
}
</script>

<style scoped>
.colorSwap{
  color: #45403C !important;
  background-color: transparent !important;
}

.logo {
  display: block;
  margin: 2rem auto;
  width: 100%;
  height: auto;
  margin-top: 7rem;
}

.registrationContainer{
  display: flex;
  flex-direction: column;
  gap: 30px;
  align-items: center;
  margin: 30px;
  max-width: 500px;
  border-radius: 12px;
  padding: 20px;
}

.registration-page {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}

.input-container {
  width: 100%;
  position: relative;
}

.registrationInput {
  font-size: 20px;
  height: 50px;
  width: 100%;
  padding: 0 15px;
  border: 2px solid;
  border-radius: 12px;
  background-color: var(--color-background-input);
  color: var(--text-primary);
  box-sizing: border-box;
}

.registrationInput.error {
  border-color: #dc3545 !important;
}

.error-message {
  color: #dc3545;
  font-size: 14px;
  margin-top: 5px;
  font-family: 'Poppins', 'Inter', sans-serif;
}

.viewPasswordIcon{
  position: absolute;
  right: 0;
  top: 0;
  width: 50px;
  height: 50px;
  color: var(--text-secondary);
  background-color: #45403C;
  padding: 7.5px;
  border-radius: 12px;
  cursor: pointer;
}

.button-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: 10px;
  gap: 15px;
}

.specialButton {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-family: 'Poppins', 'Inter', sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 120px;
}

.specialButton:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

h1 {
  color: var(--text-primary);
  font-family: 'Poppins', 'Inter', sans-serif;
  text-align: center;
  margin-bottom: 20px;
}

.registrationInput::placeholder {
  color: var(--color-border-input);
  font-family: 'Poppins', 'Inter', sans-serif;
}

.back-button {
  padding: 12px 24px;
  border: 2px solid var(--color-secondary-button);
  border-radius: 8px;
  font-size: 16px;
  font-family: 'Poppins', 'Inter', sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 120px;
  background: transparent;
  color: var(--color-secondary-button);
}

.back-button:hover {
  background: var(--color-secondary-button);
  color: var(--text-primary);
  transform: translateY(-1px);
}

/* Container layout matching LoginPage */
.container {
  display: flex;
  flex-direction: row;
  min-height: 100vh !important;
  justify-content: center;
  align-items: center;
  padding: 20px 0;
}

.container > div:first-of-type {
  display: flex;
  justify-content: center;
  width: 100%;
  max-width: 600px;
}

.container > div:last-of-type {
  flex: 1;
}
</style>